import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { Floor } from '@miq/domain/pv';

const percent = (rational: number) => Math.round(rational * 100);

@Component({
  selector: 'sp-floor-renderer, [spFloorRenderer]',
  template: `
    <!-- Floor Available -->
    <ng-container *ngIf="availableRatio > 0">
      <div class="space space--available" style="width: {{ availableRatio }}%"></div>
    </ng-container>

    <!-- Floor Occupied -->
    <ng-container *ngIf="occupiedRatio > 0">
      <div class="space space--occupied" style="width: {{ occupiedRatio }}%"></div>
    </ng-container>

    <!-- Floor Unaccounted-For -->
    <ng-container *ngIf="unnacountedForRatio > 0">
      <div class="space space--unaccounted" style="width: {{ unnacountedForRatio }}%"></div>
    </ng-container>

    <!-- Floor Non-Rentable -->
    <ng-container *ngIf="nonRentableRatio > 0">
      <div class="space space--non-rentable" style="width: {{ nonRentableRatio }}%"></div>
    </ng-container>

    <!-- No data to show -->
    <ng-container *ngIf="emptyRatio > 0">
      <div class="space" style="width: {{ emptyRatio }}%"></div>
    </ng-container>
  `,
  styleUrls: ['./floor-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FloorRendererComponent {
  @HostBinding('class') get class() {
    return 'floor';
  }
  @HostBinding('style.width') get floorWidth() {
    return `${percent(this.#floorRatio)}%`;
  }

  @Input() set floor(floor: Floor) {
    this.#setOccupiedAvailableRatios(floor);
  }

  #floorRatio = 0;
  occupiedRatio = 0;
  availableRatio = 0;
  nonRentableRatio = 0;
  unnacountedForRatio = 0;
  emptyRatio = 0;
  leftOver = 0;

  #convertRatioToPercentage = (ratio: number) => {
    const calculatedRatio = this.#floorRatio == 0 || this.#floorRatio == null ? 0 : ratio / this.#floorRatio;
    return calculatedRatio > 0 ? percent(calculatedRatio) : calculatedRatio;
  };

  #getLeftOver = (floor: Floor) => {
    const emptyRatio = this.#convertRatioToPercentage(floor.non_tgt_ratio) || 0;
    const totalRatio =
      this.occupiedRatio + this.availableRatio + this.nonRentableRatio + this.unnacountedForRatio + emptyRatio;

    if (totalRatio >= 100) {
      return emptyRatio;
    }

    return (
      this.#convertRatioToPercentage(this.#floorRatio) -
      (this.occupiedRatio + this.availableRatio + this.nonRentableRatio + this.unnacountedForRatio)
    );
  };

  #setOccupiedAvailableRatios(floor: Floor) {
    this.#floorRatio = floor.ratio ?? 0;

    this.occupiedRatio = this.#convertRatioToPercentage(floor.occupied_ratio) || 0;
    this.availableRatio = this.#convertRatioToPercentage(floor.available_ratio) || 0;
    this.nonRentableRatio = this.#convertRatioToPercentage(floor.non_rentable_ratio) || 0;
    this.unnacountedForRatio = this.#convertRatioToPercentage(floor.unaccounted_ratio) || 0;
    this.emptyRatio = this.#getLeftOver(floor);
  }
}
